// MeterPanel
//
// Shared body of the F3 Risk Meter sheet and the F1 Mirror page.
// F1 Mirror passes `oneSided` so only the participant's own data
// renders (no Them polygon, no Them legend chip). F3 passes both
// sides and additionally shows the per-axis breakdown table.
//
// Axis values are risk 0..1 (1 = highest risk / most exposed): a larger
// radar polygon means more risk, matching the F4 hotspot's
// "bigger = riskier" reading.
//
// What this component owns:
//   - legend chips (one or two)
//   - the RadarChart (size prop, default 300)
//   - the mono scale hint
//   - the per-axis breakdown table (F3 only — hidden when oneSided)
//
// D5 certainty representation is fixed to risk badges.
//
// What it does NOT own:
//   - sheet/fullsheet chrome — the close/back button, role=dialog,
//     focus trap, the eyebrow/title row, sessionLength on Mirror.
//     Those live in the caller. The point of the panel is the
//     visual sameness of the meter element, not the page.

import { meanRisk, RadarChart, riskAxisLabel } from './risk-meter.jsx';
import { severityToRiskTier } from '../../shared/risk-tiers.js';
import { Icons, RiskBadge, RiskLegend } from './ui-atoms.jsx';

export function MeterPanel({ meter, oneSided = false, onOpenDrilldown, size = 300 }) {
  const axisKeys = meter.map((row) => row.axis);
  const axisLabels = axisKeys.map(riskAxisLabel);
  const meVals = meter.map((row) => row.meValue);
  const themVals = oneSided ? undefined : meter.map((row) => row.themValue);
  const canDrillDown = typeof onOpenDrilldown === 'function';

  // Legend chip value: fixed risk badge.
  const overallValue = (value) => <RiskBadge level={severityToRiskTier(value)} />;

  // Per-axis breakdown cell: owner-filled cell around the risk badge.
  // The fill spans the full row height with no radius,
  // so the cells of a column connect into one continuous indicator block
  // per party. The transparent fill keeps Chyijia's lighter table treatment
  // while the risk badge carries the low/med/high value.
  const ownerStyles = {
    me: { bg: 'rgba(255, 255, 255, 0.2)' },
    them: { bg: 'rgba(255, 255, 255, 0.2)' },
  };
  const valueCell = (v, owner) => {
    const { bg } = ownerStyles[owner];
    return (
      <div
        data-owner={owner}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '10px 8px',
          background: bg,
        }}
      >
        <RiskBadge level={severityToRiskTier(v)} />
      </div>
    );
  };

  // Breakdown grid (F3 only): axis · you · them · chevron.
  // Risk badges fit in ~70. Zero column gap so the two
  // owner fills touch (one two-color band); the axis label and the
  // chevron cell carry their own spacing instead.
  const numericColWidth = 70;
  const gridTemplate = `1fr ${numericColWidth}px ${numericColWidth}px 24px`;

  return (
    <>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, padding: '8px 4px 16px' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{ width: 10, height: 10, background: 'oklch(55% 0.14 30)', borderRadius: 3 }}
          />
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>You</div>
            {overallValue(meanRisk(meter, 'meValue'))}
          </div>
        </div>
        {!oneSided && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 10,
                height: 10,
                border: '1.5px dashed var(--indigo)',
                borderRadius: 3,
              }}
            />
            <div style={{ fontSize: 13 }}>
              <div style={{ fontWeight: 600 }}>Them</div>
              {overallValue(meanRisk(meter, 'themValue'))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', placeItems: 'center', marginBottom: 8 }}>
        <RadarChart
          axes={axisKeys}
          axisLabels={axisLabels}
          meValues={meVals}
          themValues={themVals}
          size={size}
        />
      </div>

      {/* Per-axis breakdown (F3 only — F1 Mirror suppresses this) */}
      {!oneSided && (
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 8,
              gap: 8,
            }}
          >
            <div className="eyebrow">Axis breakdown</div>
            <RiskLegend />
          </div>
          <div
            style={{
              background: 'var(--paper)',
              border: '1px solid var(--line)',
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                gap: 0,
                padding: '0 14px',
                background: 'var(--paper-2)',
                borderBottom: '1px solid var(--line)',
                fontSize: 10,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--ink-muted)',
                fontFamily: 'IBM Plex Mono, monospace',
              }}
            >
              <span style={{ padding: '8px 10px 8px 0' }}>axis</span>
              <span
                data-owner-header="me"
                style={{
                  padding: 8,
                  background: ownerStyles.me.bg,
                  textAlign: 'right',
                  fontWeight: 'bold',
                  color: 'rgb(70, 70, 70)',
                }}
              >
                you
              </span>
              <span
                data-owner-header="them"
                style={{ padding: 8, background: ownerStyles.them.bg, textAlign: 'right' }}
              >
                them
              </span>
              <span />
            </div>
            {meter.map((row, i) => {
              const { axis, meValue, themValue } = row;
              const label = riskAxisLabel(axis);
              const onTap = canDrillDown ? () => onOpenDrilldown(axis) : undefined;
              const Wrapper = canDrillDown ? 'button' : 'div';
              return (
                <Wrapper
                  key={axis}
                  type={canDrillDown ? 'button' : undefined}
                  onClick={onTap}
                  aria-label={canDrillDown ? `Drill down into ${label}` : undefined}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    // Cells stretch to full row height and carry their own
                    // vertical padding, so the owner fills meet between rows
                    // and read as one continuous block per column.
                    alignItems: 'stretch',
                    gap: 0,
                    padding: '0 14px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--line)',
                    fontSize: 13,
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    borderTopColor: i === 0 ? 'transparent' : 'var(--line)',
                    borderTopWidth: i === 0 ? 0 : 1,
                    borderTopStyle: 'solid',
                    cursor: canDrillDown ? 'pointer' : 'default',
                    fontFamily: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <span style={{ alignSelf: 'center', padding: '10px 10px 10px 0' }}>{label}</span>
                  {valueCell(meValue, 'me')}
                  {valueCell(themValue, 'them')}
                  <span
                    style={{
                      color: canDrillDown ? 'var(--indigo)' : 'transparent',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {canDrillDown && Icons.chevron(12, 'currentColor')}
                  </span>
                </Wrapper>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
